import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type {
  DashboardCardType,
  DashboardLayout,
  DashboardLayoutItem,
  DashboardLayoutMutationType,
  DashboardLayoutStore,
  DashboardLayoutZone,
} from "../../../../types/dashboardLayout";
import { DASHBOARD_LAYOUT_VERSION } from "../../../../types/dashboardLayout";
import {
  DASHBOARD_MAX_MINI_STAT_SLOTS,
  dashboardCardRegistry,
  getDefaultDashboardLayout,
  getDefaultDashboardLayoutItems,
  type DashboardDefaultLayoutVariant,
} from "./dashboardCardRegistry";
import { canAddDashboardMiniStatCard, canRemoveDashboardLayoutCard } from "./dashboardLayoutCardPolicy";
import { coalesceSplitChartRows } from "./dashboardChartRows";
import { sanitizeLayout } from "./dashboardLayoutValidation";
import {
  localStorageDashboardLayoutAdapter,
  mergeDashboardLayoutsByRecency,
  type DashboardLayoutPersistenceAdapter,
} from "./dashboardLayoutPersistence";

interface DashboardLayoutProviderProps {
  children: React.ReactNode;
  initialLayouts?: DashboardLayout[];
  initialActiveLayoutId?: string;
  persistenceAdapter?: DashboardLayoutPersistenceAdapter;
  defaultLayoutVariant?: DashboardDefaultLayoutVariant;
}

const DashboardLayoutContext = createContext<DashboardLayoutStore | null>(null);

function makeLayoutId(): string {
  return `layout_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeLayoutName(name: string): string {
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed : "New Layout";
}

function buildUniqueName(existingNames: string[], baseName: string): string {
  const used = new Set(existingNames.map((n) => n.toLowerCase()));
  if (!used.has(baseName.toLowerCase())) return baseName;
  let i = 2;
  while (used.has(`${baseName} (${i})`.toLowerCase())) {
    i += 1;
  }
  return `${baseName} (${i})`;
}

function removeLayoutById(layouts: DashboardLayout[], layoutId: string): DashboardLayout[] {
  const remaining = layouts.filter((layout) => layout.id !== layoutId);
  if (remaining.length === 0) return layouts;
  const hasActive = remaining.some((layout) => layout.isActive);
  const nextLayouts = hasActive
    ? remaining
    : remaining.map((layout, index) => ({ ...layout, isActive: index === 0 }));
  return nextLayouts;
}

export function DashboardLayoutProvider({
  children,
  initialLayouts,
  initialActiveLayoutId,
  persistenceAdapter = localStorageDashboardLayoutAdapter,
  defaultLayoutVariant = "sync",
}: DashboardLayoutProviderProps) {
  const defaultLayout = useMemo(
    () => getDefaultDashboardLayout(defaultLayoutVariant),
    [defaultLayoutVariant]
  );
  const sanitizeOptions = useMemo(
    () => ({ defaultLayoutVariant }),
    [defaultLayoutVariant]
  );
  const didHydrateRef = useRef(false);
  /** After applying `hydrateFromRemote`, skip one remote sync so refresh does not echo PATCH to the API. */
  const skipRemotePersistLayoutsAfterHydrateRef = useRef(false);
  const skipRemotePersistActiveAfterHydrateRef = useRef(false);
  const [layouts, setLayouts] = useState<DashboardLayout[]>(() => {
    const persistedLayouts = persistenceAdapter.loadLayouts();
    if (persistedLayouts && persistedLayouts.length > 0) return persistedLayouts;
    if (!initialLayouts || initialLayouts.length === 0) return [defaultLayout];
    return initialLayouts.map((layout) => sanitizeLayout(layout, sanitizeOptions));
  });
  const [activeLayoutId, setActiveLayoutIdState] = useState<string>(
    persistenceAdapter.loadActiveLayoutId() || initialActiveLayoutId || defaultLayout.id
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [draftLayout, setDraftLayout] = useState<DashboardLayout | null>(null);
  const [isHydrating, setIsHydrating] = useState(true);
  const [remoteBootstrapFinished, setRemoteBootstrapFinished] = useState(
    () => typeof persistenceAdapter.hydrateFromRemote !== "function"
  );
  const [persistenceError, setPersistenceError] = useState<string | null>(null);
  const [pendingMutation, setPendingMutation] = useState<DashboardLayoutMutationType | null>(null);
  const [isMutating, setIsMutating] = useState(false);

  const runMutation = useCallback(<T,>(mutationType: DashboardLayoutMutationType, operation: () => T): T => {
    setPendingMutation(mutationType);
    setIsMutating(true);
    setPersistenceError(null);
    try {
      return operation();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Layout update failed.";
      setPersistenceError(message);
      throw error;
    } finally {
      window.setTimeout(() => {
        setPendingMutation((current) => (current === mutationType ? null : current));
        setIsMutating(false);
      }, 0);
    }
  }, []);

  /** Draft edits should not flash the global "Saving…" banner or shift page layout. */
  const runLayoutItemMutation = useCallback(
    <T,>(mutationType: DashboardLayoutMutationType, operation: () => T): T => {
      if (isEditMode) {
        setPersistenceError(null);
        try {
          return operation();
        } catch (error) {
          const message = error instanceof Error ? error.message : "Layout update failed.";
          setPersistenceError(message);
          throw error;
        }
      }
      return runMutation(mutationType, operation);
    },
    [isEditMode, runMutation]
  );

  useEffect(() => {
    if (didHydrateRef.current) return;
    didHydrateRef.current = true;
    setIsHydrating(true);
    setPersistenceError(null);
    try {
      const persistedLayouts = persistenceAdapter.loadLayouts();
      if (persistedLayouts && persistedLayouts.length > 0) {
        setLayouts(persistedLayouts.map((layout) => sanitizeLayout(layout, sanitizeOptions)));
      }
      const persistedActiveId = persistenceAdapter.loadActiveLayoutId();
      if (persistedActiveId) {
        setActiveLayoutIdState(persistedActiveId);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to hydrate layouts.";
      setPersistenceError(message);
    } finally {
      setIsHydrating(false);
    }
  }, [persistenceAdapter, sanitizeOptions]);

  useEffect(() => {
    setRemoteBootstrapFinished(typeof persistenceAdapter.hydrateFromRemote !== "function");
  }, [persistenceAdapter]);

  useEffect(() => {
    if (!persistenceAdapter.hydrateFromRemote) return;
    let isCancelled = false;
    setIsHydrating(true);
    setRemoteBootstrapFinished(false);
    void persistenceAdapter
      .hydrateFromRemote()
      .then((remoteState) => {
        if (isCancelled || !remoteState) return;
        if (remoteState.layouts.length > 0) {
          skipRemotePersistLayoutsAfterHydrateRef.current = true;
          setLayouts((current) =>
            mergeDashboardLayoutsByRecency(
              current,
              remoteState.layouts,
              remoteState.backendIdMap ?? {}
            ).map((layout) => sanitizeLayout(layout, sanitizeOptions))
          );
          if (remoteState.activeLayoutId) {
            skipRemotePersistActiveAfterHydrateRef.current = true;
            setActiveLayoutIdState(remoteState.activeLayoutId);
          }
        }
      })
      .catch((error) => {
        if (isCancelled) return;
        const message = error instanceof Error ? error.message : "Failed to hydrate remote layouts.";
        setPersistenceError(message);
      })
      .finally(() => {
        if (!isCancelled) {
          setRemoteBootstrapFinished(true);
          setIsHydrating(false);
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [persistenceAdapter, sanitizeOptions]);

  useEffect(() => {
    if (!remoteBootstrapFinished) return;
    try {
      const skipRemoteSync = skipRemotePersistLayoutsAfterHydrateRef.current;
      skipRemotePersistLayoutsAfterHydrateRef.current = false;
      persistenceAdapter.saveLayouts(layouts, { skipRemoteSync });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to persist layouts.";
      setPersistenceError(message);
    }
  }, [layouts, persistenceAdapter, remoteBootstrapFinished]);

  useEffect(() => {
    if (!remoteBootstrapFinished) return;
    try {
      const skipRemoteSync = skipRemotePersistActiveAfterHydrateRef.current;
      skipRemotePersistActiveAfterHydrateRef.current = false;
      persistenceAdapter.saveActiveLayoutId(activeLayoutId, { skipRemoteSync });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to persist active layout.";
      setPersistenceError(message);
    }
  }, [activeLayoutId, persistenceAdapter, remoteBootstrapFinished]);

  useEffect(() => {
    if (layouts.length === 0) return;
    if (layouts.some((layout) => layout.id === activeLayoutId)) return;
    const fallback = layouts.find((layout) => layout.isActive)?.id || layouts[0].id;
    setActiveLayoutIdState(fallback);
  }, [activeLayoutId, layouts]);

  const setActiveLayout = useCallback((layoutId: string, options?: { preserveEditorReset?: boolean }) => {
    runMutation("setActiveLayout", () => {
      setLayouts((prev) => {
        if (!prev.some((layout) => layout.id === layoutId)) return prev;
        return prev.map((layout) => ({ ...layout, isActive: layout.id === layoutId }));
      });
      setActiveLayoutIdState(layoutId);
      if (!options?.preserveEditorReset) {
        setIsEditMode(false);
        setDraftLayout(null);
      }
    });
  }, [runMutation]);

  const createLayout = useCallback((name: string, options?: { openEditor?: boolean }): string => {
    return runMutation("createLayout", () => {
      const newId = makeLayoutId();
      const uniqueName = buildUniqueName(
        layouts.map((layout) => layout.name),
        normalizeLayoutName(name)
      );
      const nowIso = new Date().toISOString();
      const createdLayout: DashboardLayout = {
        id: newId,
        name: uniqueName,
        isDefault: false,
        isActive: true,
        layoutVersion: DASHBOARD_LAYOUT_VERSION,
        items: getDefaultDashboardLayoutItems(defaultLayoutVariant),
        createdAt: nowIso,
        updatedAt: nowIso,
      };
      setLayouts((prev) => {
        return [...prev.map((layout) => ({ ...layout, isActive: false })), createdLayout];
      });
      setActiveLayoutIdState(newId);
      if (options?.openEditor) {
        setIsEditMode(true);
        setDraftLayout({ ...createdLayout, items: createdLayout.items.map((item) => ({ ...item })) });
      } else {
        setIsEditMode(false);
        setDraftLayout(null);
      }
      return newId;
    });
  }, [defaultLayoutVariant, layouts, runMutation]);

  const abandonNewLayout = useCallback((layoutId: string, fallbackLayoutId?: string) => {
    runMutation("abandonNewLayout", () => {
      setLayouts((prev) => {
        const target = prev.find((layout) => layout.id === layoutId);
        if (!target || target.isDefault) return prev;

        const nextLayouts = removeLayoutById(prev, layoutId);
        const fallback =
          (fallbackLayoutId && nextLayouts.some((layout) => layout.id === fallbackLayoutId)
            ? fallbackLayoutId
            : undefined) ?? nextLayouts.find((layout) => layout.isActive)?.id ?? nextLayouts[0]?.id;

        if (fallback) {
          setActiveLayoutIdState(fallback);
          return nextLayouts.map((layout) => ({ ...layout, isActive: layout.id === fallback }));
        }

        return nextLayouts;
      });
      setIsEditMode(false);
      setDraftLayout(null);
    });
  }, [runMutation]);

  const renameLayout = useCallback((layoutId: string, name: string) => {
    runMutation("renameLayout", () => {
      setLayouts((prev) => {
        const normalized = normalizeLayoutName(name);
        const uniqueName = buildUniqueName(
          prev.filter((layout) => layout.id !== layoutId).map((layout) => layout.name),
          normalized
        );
        const nowIso = new Date().toISOString();
        setDraftLayout((draft) => {
          if (!draft || draft.id !== layoutId) return draft;
          return { ...draft, name: uniqueName, updatedAt: nowIso };
        });
        return prev.map((layout) =>
          layout.id === layoutId
            ? { ...layout, name: uniqueName, updatedAt: nowIso }
            : layout
        );
      });
    });
  }, [runMutation]);

  const duplicateLayout = useCallback((layoutId: string): string | null => {
    return runMutation("duplicateLayout", () => {
      if (!layouts.some((layout) => layout.id === layoutId)) {
        return null;
      }
      const nextId = makeLayoutId();
      setLayouts((prev) => {
        const source = prev.find((layout) => layout.id === layoutId);
        if (!source) return prev;
        const baseName = `${source.name} Copy`;
        const uniqueName = buildUniqueName(prev.map((layout) => layout.name), baseName);
        const nowIso = new Date().toISOString();
        const duplicate: DashboardLayout = {
          ...source,
          id: nextId,
          name: uniqueName,
          isDefault: false,
          isActive: true,
          createdAt: nowIso,
          updatedAt: nowIso,
          items: source.items.map((item) => ({ ...item })),
        };
        return [...prev.map((layout) => ({ ...layout, isActive: false })), duplicate];
      });
      setActiveLayoutIdState(nextId);
      setDraftLayout(null);
      setIsEditMode(false);
      return nextId;
    });
  }, [layouts, runMutation]);

  const deleteLayout = useCallback((layoutId: string) => {
    runMutation("deleteLayout", () => {
      setLayouts((prev) => {
        const target = prev.find((layout) => layout.id === layoutId);
        if (!target || target.isDefault || prev.length <= 1) return prev;

        const remaining = prev.filter((layout) => layout.id !== layoutId);
        const hasActive = remaining.some((layout) => layout.isActive);
        const nextLayouts = hasActive
          ? remaining
          : remaining.map((layout, index) => ({ ...layout, isActive: index === 0 }));

        const nextActive = nextLayouts.find((layout) => layout.isActive)?.id ?? nextLayouts[0]?.id;
        if (nextActive) setActiveLayoutIdState(nextActive);
        return nextLayouts;
      });
      setIsEditMode(false);
      setDraftLayout(null);
    });
  }, [runMutation]);

  const setEditMode = useCallback((enabled: boolean, layoutId?: string) => {
    runMutation("setEditMode", () => {
      setIsEditMode(enabled);
      if (!enabled) {
        setDraftLayout(null);
        return;
      }
      const targetId = layoutId || activeLayoutId;
      setLayouts((prev) => {
        const active = prev.find((layout) => layout.id === targetId) ?? prev[0];
        setDraftLayout(active ? { ...active, items: active.items.map((item) => ({ ...item })) } : null);
        return prev;
      });
    });
  }, [activeLayoutId, runMutation]);

  const saveEditModeChanges = useCallback(() => {
    runMutation("saveEditModeChanges", () => {
      if (!draftLayout) {
        setIsEditMode(false);
        return;
      }
      const savedLayout = sanitizeLayout(
        { ...draftLayout, updatedAt: new Date().toISOString() },
        sanitizeOptions
      );
      const nextLayouts = layouts.map((layout) =>
        layout.id === savedLayout.id ? savedLayout : layout
      );
      setLayouts(nextLayouts);
      persistenceAdapter.saveLayouts(nextLayouts);
      setIsEditMode(false);
      setDraftLayout(null);
    });
  }, [draftLayout, layouts, persistenceAdapter, runMutation, sanitizeOptions]);

  const cancelEditModeChanges = useCallback(() => {
    runMutation("cancelEditModeChanges", () => {
      setIsEditMode(false);
      setDraftLayout(null);
    });
  }, [runMutation]);

  const mutateItems = useCallback(
    (layoutId: string, updater: (items: DashboardLayoutItem[]) => DashboardLayoutItem[]) => {
      if (isEditMode) {
        setDraftLayout((prevDraft) => {
          if (!prevDraft || prevDraft.id !== layoutId) return prevDraft;
          return {
            ...prevDraft,
            items: updater(prevDraft.items.map((item) => ({ ...item }))),
            updatedAt: new Date().toISOString(),
          };
        });
        return;
      }
      setLayouts((prev) =>
        prev.map((layout) => {
          if (layout.id !== layoutId) return layout;
          return {
            ...layout,
            items: updater(layout.items.map((item) => ({ ...item }))),
            updatedAt: new Date().toISOString(),
          };
        })
      );
    },
    [isEditMode]
  );

  const updateLayoutItems = useCallback((layoutId: string, items: DashboardLayoutItem[]) => {
    runLayoutItemMutation("updateLayoutItems", () => {
      const normalizedItems = sanitizeLayout({ id: layoutId, items }, sanitizeOptions).items;
      mutateItems(layoutId, () => normalizedItems);
    });
  }, [mutateItems, runLayoutItemMutation, sanitizeOptions]);

  const addCard = useCallback((
    layoutId: string,
    cardType: DashboardCardType,
    targetZone?: DashboardLayoutZone
  ) => {
    runLayoutItemMutation("addCard", () => {
      mutateItems(layoutId, (items) => {
        const registryEntry = dashboardCardRegistry[cardType];
        if (targetZone && !registryEntry.compatibleZones.includes(targetZone)) {
          return items;
        }
        const zone = targetZone || registryEntry.defaultItem.zone;
        if (zone === "miniStats") {
          const visibleMiniTypes = items
            .filter((item) => item.zone === "miniStats" && item.visible)
            .map((item) => item.cardType);
          if (!canAddDashboardMiniStatCard(cardType, visibleMiniTypes)) {
            return items;
          }
        } else if (items.some((item) => item.cardType === cardType && item.visible)) {
          return items;
        }
        if (zone === "miniStats") {
          const visibleMiniCount = items.filter(
            (item) => item.zone === "miniStats" && item.visible
          ).length;
          if (visibleMiniCount >= DASHBOARD_MAX_MINI_STAT_SLOTS) return items;
        }
        const fallback = registryEntry.defaultItem;
        const nextItems = items.map((item) =>
          item.cardType === cardType
            ? {
                ...item,
                visible: true,
                zone: targetZone || item.zone || fallback.zone,
              }
            : item
        );
        if (!nextItems.some((item) => item.cardType === cardType)) {
          const nextOrder =
            zone === "miniStats"
              ? nextItems
                  .filter((item) => item.zone === "miniStats" && item.visible)
                  .reduce((max, item) => Math.max(max, item.order ?? -1), -1) + 1
              : fallback.order;
          nextItems.push({ ...fallback, visible: true, zone, order: nextOrder });
        }
        return nextItems;
      });
    });
  }, [mutateItems, runLayoutItemMutation]);

  const removeCard = useCallback((layoutId: string, cardType: DashboardCardType) => {
    runLayoutItemMutation("removeCard", () => {
      const active = isEditMode && draftLayout?.id === layoutId
        ? draftLayout
        : layouts.find((layout) => layout.id === layoutId);
      const zone = active?.items.find((item) => item.cardType === cardType)?.zone;
      if (!zone || !canRemoveDashboardLayoutCard(cardType, zone)) return;
      if (!dashboardCardRegistry[cardType].removable) return;
      mutateItems(layoutId, (items) => {
        const target = items.find((item) => item.cardType === cardType);
        if (target?.zone === "miniStats") return items;

        return coalesceSplitChartRows(
          items.map((item) =>
            item.cardType === cardType ? { ...item, visible: false } : item
          )
        );
      });
    });
  }, [draftLayout, isEditMode, layouts, mutateItems, runLayoutItemMutation]);

  const resetToDefault = useCallback((layoutId?: string) => {
    runMutation("resetToDefault", () => {
      const targetLayoutId = layoutId || activeLayoutId;
      setLayouts((prev) =>
        prev.map((layout) =>
          layout.id === targetLayoutId
            ? {
                ...layout,
                items: getDefaultDashboardLayoutItems(defaultLayoutVariant),
                updatedAt: new Date().toISOString(),
              }
            : layout
        )
      );
      setDraftLayout(null);
      setIsEditMode(false);
    });
  }, [activeLayoutId, defaultLayoutVariant, runMutation]);

  const getActiveLayout = useCallback((): DashboardLayout | null => {
    if (isEditMode && draftLayout && draftLayout.id === activeLayoutId) {
      return draftLayout;
    }
    return layouts.find((layout) => layout.id === activeLayoutId) ?? layouts[0] ?? null;
  }, [activeLayoutId, draftLayout, isEditMode, layouts]);

  const getVisibleItems = useCallback(
    (layoutId?: string): DashboardLayoutItem[] => {
      const targetId = layoutId || activeLayoutId;
      if (isEditMode && draftLayout && draftLayout.id === targetId) {
        return draftLayout.items.filter((item) => item.visible);
      }
      const target = layouts.find((layout) => layout.id === targetId);
      if (!target) return [];
      return target.items.filter((item) => item.visible);
    },
    [activeLayoutId, draftLayout, isEditMode, layouts]
  );

  const getLayoutItems = useCallback(
    (layoutId?: string): DashboardLayoutItem[] => {
      const targetId = layoutId || activeLayoutId;
      if (isEditMode && draftLayout && draftLayout.id === targetId) {
        return draftLayout.items;
      }
      const target = layouts.find((layout) => layout.id === targetId);
      return target?.items ?? [];
    },
    [activeLayoutId, draftLayout, isEditMode, layouts]
  );

  const canDeleteLayout = useCallback(
    (layoutId: string): boolean => {
      const target = layouts.find((layout) => layout.id === layoutId);
      if (!target) return false;
      if (target.isDefault) return false;
      return layouts.length > 1;
    },
    [layouts]
  );

  const value = useMemo<DashboardLayoutStore>(
    () => ({
      layouts,
      activeLayoutId,
      isEditMode,
      draftLayout,
      isHydrating,
      persistenceError,
      pendingMutation,
      isMutating,
      setActiveLayout,
      createLayout,
      abandonNewLayout,
      renameLayout,
      duplicateLayout,
      deleteLayout,
      setEditMode,
      updateLayoutItems,
      addCard,
      removeCard,
      resetToDefault,
      saveEditModeChanges,
      cancelEditModeChanges,
      getActiveLayout,
      getLayoutItems,
      getVisibleItems,
      canDeleteLayout,
    }),
    [
      layouts,
      activeLayoutId,
      isEditMode,
      draftLayout,
      isHydrating,
      persistenceError,
      pendingMutation,
      isMutating,
      setActiveLayout,
      createLayout,
      abandonNewLayout,
      renameLayout,
      duplicateLayout,
      deleteLayout,
      setEditMode,
      updateLayoutItems,
      addCard,
      removeCard,
      resetToDefault,
      saveEditModeChanges,
      cancelEditModeChanges,
      getActiveLayout,
      getLayoutItems,
      getVisibleItems,
      canDeleteLayout,
    ]
  );

  return <DashboardLayoutContext.Provider value={value}>{children}</DashboardLayoutContext.Provider>;
}

export function useDashboardLayoutStore(): DashboardLayoutStore {
  const context = useContext(DashboardLayoutContext);
  if (!context) {
    throw new Error("useDashboardLayoutStore must be used within DashboardLayoutProvider");
  }
  return context;
}
